import type { SanityDocument } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";
import clsx from "clsx";
import { useBlogPostPublishDate } from "~/hooks/useBlogPostPublishDate";
import { dataset, projectId } from "~/sanity";
import type { Author } from "~/types";
import BlogHero from "./BlogHero";

export default function BlogPreviews({
  posts,
  authors,
}: {
  posts: SanityDocument[];
  authors: Author[];
}) {
  const builder = imageUrlBuilder({ projectId, dataset });
  const formatPublishDate = useBlogPostPublishDate();
  const getAuthorData = (authorId: string): Author => {
    return authors.filter((author) => author._id === authorId)[0];
  };
  const featuredPost = posts[0];
  const featuredAuthor = getAuthorData(featuredPost.author._ref);

  return (
    <section
      className={clsx(["relative w-full bg-white", "md:pb-40", "2xl:pb-60"])}
    >
      <BlogHero
        author={featuredAuthor}
        date={formatPublishDate(featuredPost.publishDate)}
        title={featuredPost.title}
        href={`/articles/${featuredPost.slug.current}`}
      />
      <div
        className={clsx([
          "mx-auto mt-20 grid max-w-6xl grid-cols-1 gap-20 px-6 pb-32",
          "mx-12 md:grid-cols-2 md:gap-24 md:px-12",
          "lg:px-2",
        ])}
      >
        {posts.slice(1).map((post) => {
          const author = getAuthorData(post.author._ref);
          return (
            <div key={post._id}>
              <div className="flex h-full flex-col">
                <a
                  key={post._id}
                  href={`/articles/${post.slug.current}`}
                  className={clsx(["mb-4 hover:underline"])}
                >
                  <h3 className="font-logo text-3xl">{post.title}</h3>
                </a>
                <div className="mt-auto flex flex-row">
                  {author && (
                    <div className="mr-3">
                      <div className="h-[50px] w-[50px] rounded-full bg-tan-lightest">
                        <img
                          src={builder
                            .image(author.image)
                            .width(100)
                            .height(100)
                            .quality(80)
                            .url()}
                          alt={author.name}
                          className="h-full w-full rounded-full"
                          itemProp="image"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    {author && (
                      <p className="font-apercu text-text-gray">
                        {author.name}
                      </p>
                    )}
                    <div className="font-apercu text-text-gray">
                      {formatPublishDate(post.publishDate)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
